'use client'

import { useEffect, useState } from 'react'

/**
 * A value that settles instead of following every keystroke.
 *
 * The input itself stays uncontrolled by this — it must repaint on every
 * character or typing feels broken. What waits is everything DOWNSTREAM of it:
 * the filtering, the result count and its `aria-live` announcement (which, read
 * per keystroke, turns a screen reader into a stutter), and — the moment the
 * catalogue moves out of `lib/rental.ts` and into a query — the request.
 *
 * `delay` is deliberately short. This is a typing pause, not a throttle: long
 * enough that "obiektyw" is one settle rather than eight, short enough that the
 * list has already moved by the time the eye gets back to it.
 */
export function useDebouncedValue<T>(value: T, delay = 200): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return settled
}
